import React, { useEffect, useMemo, useState } from "react";
import { useACPP } from "../ACPPContext";
import {
  ModuleCard, ModuleHeader, SectionDivider, SoftPanel, InfoBanner,
  MetricCard, Grid4, Tag, TableChip, BtnRow, BtnSecondary, BtnLight, FormInput, TableWrap, Th, Td,
} from "../components/ui";

export default function Module12LeadershipDashboard() {
  const {
    schoolMaster,
    selectedGrades,
    subjectMappings,
    module5OwnershipMappings,
    module6Plans,
    module7Assessments,
    module8DevPlans,
    module8TrainingPlans,
    module9DevPlans,
    module10CalendarRows,
    module11State,
    module12Snapshot,
    canEditModules1to5,
    module12Loading,
    module12LoadError,
    loadModule12LeadershipDashboard,
    saveModule12LeadershipDashboard,
    resetModule12LeadershipDashboard,
    getM6FlatRows,
  } = useACPP();

  const [summaryNotes, setSummaryNotes] = useState("");
  const [savedSnapshot, setSavedSnapshot] = useState({
    kpi: {},
    healthRows: [],
    chainSteps: [],
    summaryNotes: "",
  });
  const [moduleStatus, setModuleStatus] = useState("Pending");
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState("");

  // ── Data derivations ─────────────────────────────────────────────────────
  const staffSet = useMemo(() => {
    const s = new Set();
    module5OwnershipMappings.forEach(r => {
      (r.teachers || []).forEach(t => { if (t) s.add(t); });
      (r.hods || []).forEach(h => { if (h) s.add(h); });
    });
    return s;
  }, [module5OwnershipMappings]);

  const m6FlatRows = useMemo(() => {
    if (typeof getM6FlatRows === "function") return getM6FlatRows();
    return [];
  }, [getM6FlatRows]);

  const sectionTimetableCount = Object.keys(module11State?.sectionTimetables || {}).length;

  // ── KPI values ───────────────────────────────────────────────────────────
  const kpi = {
    year: schoolMaster.year || "—",
    grades: selectedGrades.length,
    subjects: subjectMappings.length,
    staff: staffSet.size,
    chapters: m6FlatRows.length,
    assessments: module7Assessments.length,
    calendar: module10CalendarRows.length,
    timetable: sectionTimetableCount,
  };

  // ── Module health rows ────────────────────────────────────────────────────
  const healthRows = useMemo(() => [
    {
      mod: "M1", name: "School Master Details",
      ok: !!schoolMaster.year,
      count: schoolMaster.year || "—",
      note: schoolMaster.year ? "Academic year set" : "⚠ Set academic year in Module 1",
    },
    {
      mod: "M2", name: "Setup Logic",
      ok: true,
      count: "Config",
      note: "Static reference module",
    },
    {
      mod: "M3", name: "Grades Offered",
      ok: selectedGrades.length > 0,
      count: `${selectedGrades.length} grade(s)`,
      note: selectedGrades.length ? selectedGrades.slice(0, 4).join(", ") : "⚠ Select grades in Module 3",
    },
    {
      mod: "M4", name: "Academic Planning Stage",
      ok: subjectMappings.length > 0,
      count: `${subjectMappings.length} subject(s)`,
      note: subjectMappings.length ? "Subjects mapped" : "⚠ Add subjects in Module 4",
    },
    {
      mod: "M5", name: "Academic Ownership",
      ok: staffSet.size > 0,
      count: `${staffSet.size} staff`,
      note: staffSet.size ? "Teachers & HODs assigned" : "⚠ Assign teachers in Module 5",
    },
    {
      mod: "M6", name: "Teacher Academic Planning",
      ok: m6FlatRows.length > 0,
      count: `${m6FlatRows.length} chapter(s)`,
      note: m6FlatRows.length ? "Chapter plan active" : "⚠ Add chapter rows in Module 6",
    },
    {
      mod: "M7", name: "Assessment Structure",
      ok: module7Assessments.length > 0,
      count: `${module7Assessments.length} row(s)`,
      note: module7Assessments.length ? "Assessments mapped" : "⚠ Add assessments in Module 7",
    },
    {
      mod: "M8", name: "Teacher Development & Training",
      ok: module8DevPlans.length > 0 || module8TrainingPlans.length > 0,
      count: `${module8DevPlans.length + module8TrainingPlans.length} plan(s)`,
      note: (module8DevPlans.length + module8TrainingPlans.length) ? "Plans active" : "ⓘ Add plans in Module 8 if needed",
    },
    {
      mod: "M9", name: "Student Development Plan",
      ok: module9DevPlans.length > 0,
      count: `${module9DevPlans.length} plan(s)`,
      note: module9DevPlans.length ? "Plans active" : "ⓘ Add plans in Module 9 if needed",
    },
    {
      mod: "M10", name: "Academic Calendar",
      ok: module10CalendarRows.length > 0,
      count: `${module10CalendarRows.length} row(s)`,
      note: module10CalendarRows.length ? "Calendar generated" : "⚠ Generate calendar in Module 10",
    },
    {
      mod: "M11", name: "Dynamic Timetable Builder",
      ok: sectionTimetableCount > 0,
      count: `${sectionTimetableCount} section(s)`,
      note: sectionTimetableCount ? "Timetable generated" : "Generate timetable in Module 11",
    },
  ], [schoolMaster, selectedGrades, subjectMappings, staffSet, m6FlatRows, module7Assessments, module8DevPlans, module8TrainingPlans, module9DevPlans, module10CalendarRows, sectionTimetableCount]);

  // ── Planning chain steps ──────────────────────────────────────────────────
  const chainSteps = useMemo(() => [
    { label: "M1: Academic Year",  ok: !!schoolMaster.year },
    { label: "M3: Grades",         ok: selectedGrades.length > 0 },
    { label: "M4: Subjects",       ok: subjectMappings.length > 0 },
    { label: "M5: Ownership",      ok: staffSet.size > 0 },
    { label: "M6: Chapter Plan",   ok: m6FlatRows.length > 0 },
    { label: "M7: Assessments",    ok: module7Assessments.length > 0 },
    { label: "M10: Calendar",      ok: module10CalendarRows.length > 0 },
    { label: "M11: Timetable",     ok: sectionTimetableCount > 0 },
  ], [schoolMaster, selectedGrades, subjectMappings, staffSet, m6FlatRows, module7Assessments, module10CalendarRows, sectionTimetableCount]);

  const liveSnapshot = useMemo(() => ({
    kpi,
    healthRows: healthRows.map((row) => ({ mod: row.mod, name: row.name, ok: row.ok, count: row.count, note: row.note })),
    chainSteps: chainSteps.map((row) => ({ label: row.label, ok: row.ok })),
    summaryNotes: String(summaryNotes || "").trim(),
  }), [kpi, healthRows, chainSteps, summaryNotes]);

  useEffect(() => {
    setSummaryNotes(module12Snapshot?.summaryNotes || "");
    setSavedSnapshot({
      kpi: module12Snapshot?.kpi || {},
      healthRows: module12Snapshot?.healthRows || [],
      chainSteps: module12Snapshot?.chainSteps || [],
      summaryNotes: module12Snapshot?.summaryNotes || "",
    });
  }, [module12Snapshot]);

  useEffect(() => {
    const hasSaved = Object.keys(savedSnapshot.kpi || {}).length > 0
      || (savedSnapshot.healthRows || []).length > 0
      || (savedSnapshot.chainSteps || []).length > 0
      || !!String(savedSnapshot.summaryNotes || "").trim();
    if (!hasSaved) {
      setModuleStatus("Pending");
      return;
    }
    if (JSON.stringify(liveSnapshot) === JSON.stringify(savedSnapshot)) {
      setModuleStatus("Saved");
      return;
    }
    setModuleStatus("In Progress");
  }, [liveSnapshot, savedSnapshot]);

  const save = async () => {
    setSaving(true);
    setActionError("");
    try {
      const saved = await saveModule12LeadershipDashboard(liveSnapshot);
      const nextSaved = {
        kpi: saved?.kpi || {},
        healthRows: saved?.healthRows || [],
        chainSteps: saved?.chainSteps || [],
        summaryNotes: saved?.summaryNotes || "",
      };
      setSavedSnapshot(nextSaved);
      alert("Leadership dashboard snapshot saved for current ACPP year.");
    } catch (error) {
      setActionError(error?.message || "Failed to save Module 12 snapshot.");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    setSaving(true);
    setActionError("");
    try {
      await loadModule12LeadershipDashboard();
    } catch (error) {
      setActionError(error?.message || "Failed to refresh Module 12 snapshot.");
    } finally {
      setSaving(false);
    }
  };

  const resetYearData = async () => {
    setSaving(true);
    setActionError("");
    try {
      await resetModule12LeadershipDashboard();
      setSummaryNotes("");
      setSavedSnapshot({ kpi: {}, healthRows: [], chainSteps: [], summaryNotes: "" });
    } catch (error) {
      setActionError(error?.message || "Failed to reset Module 12 snapshot.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModuleCard id="module12">
      <ModuleHeader
        step="Module 12"
        title="Leadership Dashboard & Integration Layer"
        description="Live summary across all 11 modules. Auto-refreshes whenever any module saves data. This section provides a unified leadership view of the entire academic planning portal."
        showStatus={false}
      />

      <InfoBanner>
        <strong>Integration note:</strong> This is the orchestration layer. It reads live data from all modules via shared React context and presents a unified leadership view.
      </InfoBanner>
      {!!(module12LoadError || actionError) && (
        <div className="mt-3 rounded-xl border border-[#fecaca] bg-[#fff1f2] text-[#9f1239] px-3 py-2 text-sm">
          {module12LoadError || actionError}
        </div>
      )}

      <SectionDivider />

      {canEditModules1to5 ? (
        <SoftPanel title="Leadership Snapshot Controls">
          <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">Capture this dashboard state for the active year with notes and refresh/reset controls.</p>
          <FormInput
            value={summaryNotes}
            onChange={(e) => setSummaryNotes(e.target.value)}
            placeholder="Optional leadership notes for this year's planning snapshot"
            disabled={!canEditModules1to5}
          />
          <BtnRow>
            <BtnSecondary disabled={!canEditModules1to5 || saving || module12Loading} onClick={save}>Save Year Snapshot</BtnSecondary>
            <BtnLight disabled={saving || module12Loading} onClick={refresh}>Refresh Year Data</BtnLight>
          </BtnRow>
        </SoftPanel>
      ) : (
        <SoftPanel title="Leadership Snapshot Notes">
          <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">Saved notes for the current year snapshot.</p>
          <p className="text-[13px] text-[#1f2937] leading-[1.7] m-0">{summaryNotes || "No notes saved for this year."}</p>
        </SoftPanel>
      )}

      <SectionDivider />

      {/* KPI Strip Row 1 */}
      <Grid4>
        <MetricCard label="Academic Year"   value={kpi.year}      meta="From Module 1" />
        <MetricCard label="Status" value={moduleStatus} meta="Current year snapshot status" />
        <MetricCard label="Grades Active"   value={kpi.grades}    meta="From Module 3" />
        <MetricCard label="Subjects Mapped" value={kpi.subjects}  meta="From Module 4" />
      </Grid4>

      <SectionDivider />

      <SoftPanel title="Cross-Module KPI Snapshot">
        <TableWrap>
          <thead>
            <tr>
              <Th>Source</Th>
              <Th>Metric</Th>
              <Th>Value</Th>
              <Th>Meaning</Th>
            </tr>
          </thead>
          <tbody>
            <tr><Td><TableChip>M5</TableChip></Td><Td>Staff Assigned</Td><Td>{kpi.staff}</Td><Td>Teachers and HODs mapped</Td></tr>
            <tr><Td><TableChip>M6</TableChip></Td><Td>Chapter Rows</Td><Td>{kpi.chapters}</Td><Td>Academic plan chapter entries</Td></tr>
            <tr><Td><TableChip>M7</TableChip></Td><Td>Assessments</Td><Td>{kpi.assessments}</Td><Td>Assessment structure rows</Td></tr>
            <tr><Td><TableChip>M10</TableChip></Td><Td>Calendar Rows</Td><Td>{kpi.calendar}</Td><Td>Calendar events generated</Td></tr>
            <tr><Td><TableChip>M11</TableChip></Td><Td>Timetable Sections</Td><Td>{kpi.timetable}</Td><Td>Sections with timetable data</Td></tr>
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      {/* Module Health Table */}
      <SoftPanel title="Module Health Status">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">Live status of all 11 data-producing modules.</p>
        <TableWrap>
          <thead>
            <tr>
              <Th>Module</Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Count</Th>
              <Th>Notes</Th>
            </tr>
          </thead>
          <tbody>
            {healthRows.map((r) => (
              <tr key={r.mod}>
                <Td><TableChip>{r.mod}</TableChip></Td>
                <Td>{r.name}</Td>
                <Td>
                  <span
                    className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-semibold text-white ${r.ok ? "bg-[#1f8a46]" : "bg-[#f57c00]"}`}
                  >
                    {r.ok ? "✓ Active" : "⚠ Pending"}
                  </span>
                </Td>
                <Td>{r.count}</Td>
                <Td className="text-[11px] text-[#6b7280]">{r.note}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </SoftPanel>

      <SectionDivider />

      {/* Planning Chain */}
      <SoftPanel title="Planning Chain Readiness">
        <p className="text-[13px] text-[#6b7280] leading-[1.7] m-0 mb-3">Each step depends on the one before it. Green = complete, Yellow = pending.</p>
        <div className="flex flex-wrap gap-2 items-center">
          {chainSteps.map((step, i) => (
            <React.Fragment key={step.label}>
              <Tag color={step.ok ? "green" : "yellow"}>{step.label}</Tag>
              {i < chainSteps.length - 1 && (
                <span className="text-[#9ca3af] text-lg px-1">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </SoftPanel>

    </ModuleCard>
  );
}
